import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle
}: FAQItemProps) {
  return <div className="border-b border-gray-200" data-id="element-21">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-6 text-left cursor-pointer group" aria-expanded={isOpen} data-id="element-22">
        <span className="text-lg font-semibold text-[#1A1A1A] pr-8 group-hover:text-[#C8A04A] transition-colors duration-200" data-id="element-23">
          {question}
        </span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} className="flex-shrink-0" data-id="element-24">
          <ChevronDownIcon className="w-5 h-5 text-[#C8A04A]" data-id="element-25" />
        </motion.div>
      </button>
      <AnimatePresence initial={false} data-id="element-26">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-27">
            <div className="pb-6 text-[#4B5563] leading-relaxed whitespace-pre-line" data-id="element-28">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="bg-[#FAFAF8] py-20 md:py-28" data-id="element-29">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-100px'
    }} transition={{
      duration: 0.6
    }} className="max-w-3xl mx-auto px-6" data-id="element-30">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] tracking-tight mb-12" data-id="element-31">
          Frequently Asked Questions
        </h2>

        <div className="border-t border-gray-200" data-id="element-32">
          <FAQItem question="Is this better than finasteride?" answer="They operate through completely different mechanisms. Finasteride has a stronger evidence base for stopping DHT-mediated loss in most men. CrownControl™ works on cellular energy — no hormonal involvement, zero risk of sexual side effects. For men who have ruled out finasteride, this is not plan B. It's a different pathway. Many men use both; that decision is between you and your doctor." isOpen={openIndex === 0} onToggle={() => handleToggle(0)} data-id="element-33" />
          <FAQItem question="Can I use this with minoxidil?" answer="Yes. Photobiomodulation has been studied as an adjunct to topical therapies and shows additive benefit in several trials. CrownControl™ does not interfere with minoxidil." isOpen={openIndex === 1} onToggle={() => handleToggle(1)} data-id="element-34" />
          <FAQItem question="What if I'm too far gone?" answer="CrownControl™ is most effective for men in the early to moderate stages of androgenetic alopecia who still have miniaturized follicles in the affected areas. If the areas have been fully inactive for many years and are completely smooth, reactivation becomes significantly less likely. If you're uncertain about your stage, consult a dermatologist before purchasing. We'd rather lose a sale than overpromise to someone we can't help." isOpen={openIndex === 2} onToggle={() => handleToggle(2)} data-id="element-35" />
          <FAQItem question="How long until I see results?" answer="Realistic timeline: reduced scalp irritation within 2–4 weeks. Reduced shedding and follicle stabilization around months 3–4. Measurable density improvements possible in months 4–6 for consistent users. Hair biology operates slowly. Anyone telling you otherwise isn't being honest." isOpen={openIndex === 3} onToggle={() => handleToggle(3)} data-id="element-36" />
          <FAQItem question="What wavelength does it use?" answer="CrownControl™ operates at 650–670nm — the validated photobiomodulation window cited consistently in peer-reviewed literature and FDA clearance submissions for hair-loss devices. Devices outside this range are not delivering equivalent therapy." isOpen={openIndex === 4} onToggle={() => handleToggle(4)} data-id="element-37" />
          <FAQItem question="What does FDA clearance actually mean?" answer="At-home hair-loss devices in the FDA-cleared device class have gone through a substantive regulatory review — not a marketing badge. Manufacturers submit clinical evidence for a specific indication: hair loss classification, patient age range, skin phototype. CrownControl™ is in this cleared class. Clearance documentation is available on request." isOpen={openIndex === 5} onToggle={() => handleToggle(5)} data-id="element-38" />
          <FAQItem question="Do I have to use it forever?" answer="Hair loss is an ongoing biological process. If you stop any intervention, the underlying factors continue. Most men who see results choose to maintain the protocol because the outcome is worth 10 minutes. We won't tell you this is a one-time cure. But 10 minutes a day to protect a result you've spent months achieving is a trade most men are happy to make." isOpen={openIndex === 6} onToggle={() => handleToggle(6)} data-id="element-39" />
        </div>
      </motion.div>
    </section>;
}